import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, Users, ShoppingBag } from 'lucide-react';
import listingService, { ListingEntry, ListingType } from '../../services/listingService';
import ThumbnailImage from '../ThumbnailImage';

// ============================================
// "Neue Vereine" / "Neue Spielstätten" / "Neue Shops" – drei horizontale
// Scroll-Sektionen am Ende der Startseite (unter "Aus der Community").
// EIN Fetch für alle drei Gruppen (getRecentEntries ist teuer — 5 API-Calls).
// Kartenstil wie "Neue Turniere", nur ohne Datums-Chip.
// ============================================

const LIMIT_PER_GROUP = 10;

const GROUPS: { type: ListingType; title: string; Icon: typeof Users }[] = [
  { type: 'club', title: 'Neue Vereine', Icon: Users },
  { type: 'pub', title: 'Neue Spielstätten', Icon: MapPin },
  { type: 'shop', title: 'Neue Shops', Icon: ShoppingBag },
];

const HomeNewListingGroups: React.FC = () => {
  const [entries, setEntries] = useState<ListingEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    listingService
      .getRecentEntries(80)
      .then((list) => {
        if (!cancelled) setEntries(list);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // CLS-Fix (2026-09-01): während des Ladens Skeleton-Reihen in Endhöhe
  // (Karten h-[210px]) für die drei Gruppen rendern statt null
  if (loading) {
    return (
      <>
        {GROUPS.map((group) => (
          <section key={group.type} aria-label={group.title} aria-hidden="true">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="section-heading text-base font-bold text-white">{group.title}</h2>
            </div>
            <div className="flex gap-3 overflow-hidden pb-1">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[210px] w-[240px] shrink-0 animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900"
                />
              ))}
            </div>
          </section>
        ))}
      </>
    );
  }

  if (entries.length === 0) return null;

  return (
    <>
      {GROUPS.map((group) => {
        const groupEntries = entries
          .filter((e) => e.type === group.type)
          .slice(0, LIMIT_PER_GROUP);
        if (groupEntries.length === 0) return null;
        return (
          <ListingRow key={group.type} title={group.title} entries={groupEntries} />
        );
      })}
    </>
  );
};

const ListingRow: React.FC<{ title: string; entries: ListingEntry[] }> = ({
  title,
  entries,
}) => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <section aria-label={title}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="section-heading text-base font-bold text-white">{title}</h2>
        {entries.length > 3 && (
          <div className="hidden items-center gap-1.5 sm:flex">
            <button
              type="button"
              aria-label="Zurück"
              onClick={() => scrollBy(-360)}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white"
            >
              <ChevronLeft size={15} />
            </button>
            <button
              type="button"
              aria-label="Weiter"
              onClick={() => scrollBy(360)}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {entries.map((entry) => (
          <ListingCard
            key={`${entry.type}-${entry.id}`}
            entry={entry}
            onClick={() => {
              const route = listingService.routeFor(entry);
              if (route) navigate(route);
            }}
          />
        ))}
      </div>
    </section>
  );
};

const ListingCard: React.FC<{ entry: ListingEntry; onClick: () => void }> = ({
  entry,
  onClick,
}) => {
  const [imgError, setImgError] = useState(false);
  const showImage = entry.image && !imgError;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex h-[210px] w-[240px] shrink-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left transition-colors hover:border-[#3fced1]/50"
    >
      <div className="relative h-[124px] w-full shrink-0 overflow-hidden bg-zinc-800">
        {showImage ? (
          // 240px-Karte → 400er-Thumbnail statt Original (Fallback: Original)
          <ThumbnailImage
            src={entry.image as string}
            thumbSize={400}
            alt=""
            loading="lazy"
            onError={() => setImgError(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
            <img
              src="/icon.png"
              alt=""
              loading="lazy"
              className="h-10 w-10 rounded-lg shadow-[0_0_24px_rgba(63,206,210,0.25)]"
            />
          </div>
        )}
        {/* Logo-Overlay (z.B. Vereinswappen) */}
        {entry.logo && (
          <div className="absolute bottom-1.5 right-1.5 h-8 w-8 overflow-hidden rounded-md border border-zinc-700/60 bg-zinc-900 shadow-sm">
            <ThumbnailImage
              src={entry.logo}
              thumbSize={100}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-2.5">
        <h3 className="line-clamp-2 text-[13px] font-semibold leading-snug text-white transition-colors group-hover:text-[#3fced1]">
          {entry.name}
        </h3>
        {entry.location && entry.location !== 'Unbekannt' && (
          <div className="mt-auto flex items-center gap-1 pt-1.5 text-[11px] text-gray-500">
            <MapPin size={11} className="shrink-0" />
            <span className="truncate">{entry.location}</span>
          </div>
        )}
      </div>
    </button>
  );
};

export default HomeNewListingGroups;
